import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn, displayName } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useHostCandidates } from "@/hooks/useHostCandidates";

interface HostSelectProps {
  value?: string;
  hostType: string;
  onChange: (hostId: string) => void;
  onHostTypeChange: (hostType: string) => void;
}

const PRESETS = [
  { id: "nimi", label: "Nimi" },
  { id: "member", label: "Member" },
  { id: "museum", label: "Museum" },
];

const HostSelect = ({ value, onChange, hostType, onHostTypeChange }: HostSelectProps) => {
  const [open, setOpen] = useState(false);
  const { data: hosts, isLoading } = useHostCandidates([value]);

  const handleHostChange = (hostId: string) => {
    onChange(hostId);
    setOpen(false);
    if (hostId === "nimi" || hostId === "museum" || hostId === "member") {
      onHostTypeChange("member");
    }
  };

  const hostLabel = (host: { first_name: string | null; last_name: string | null; member_type: string | null }) =>
    `${displayName(host.first_name ?? undefined, host.last_name ?? undefined)}${
      host.member_type === "artist" ? " (Artist)" : ""
    }`;

  const preset = PRESETS.find((p) => p.id === value);
  const selectedHost = hosts?.find((h) => h.id === value);
  const triggerLabel = preset
    ? preset.label
    : selectedHost
    ? hostLabel(selectedHost)
    : isLoading && value
    ? "Loading..."
    : "Select a host";

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="hostId">Host</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="hostId"
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between bg-card border-input font-normal"
            >
              <span className={cn(!value && "text-muted-foreground")}>{triggerLabel}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-popover" align="start">
            <Command>
              <CommandInput placeholder="Type host name to search..." />
              <CommandList>
                <CommandEmpty>{isLoading ? "Loading hosts..." : "No host found."}</CommandEmpty>
                <CommandGroup heading="Presets">
                  {PRESETS.map((p) => (
                    <CommandItem key={p.id} value={p.label} onSelect={() => handleHostChange(p.id)}>
                      <Check className={cn("mr-2 h-4 w-4", value === p.id ? "opacity-100" : "opacity-0")} />
                      {p.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup heading="Hosts">
                  {(hosts ?? []).map((host) => (
                    <CommandItem
                      key={host.id}
                      value={`${hostLabel(host)} ${host.id}`}
                      onSelect={() => handleHostChange(host.id)}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value === host.id ? "opacity-100" : "opacity-0")} />
                      {hostLabel(host)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-xs text-muted-foreground">
          Only people set to Host or Artist appear here. Upgrade a member in the Users tab to add them.
        </p>
      </div>

      {/* Host Type selector (only shown for custom real user hosts) */}
      {value && value !== "nimi" && value !== "museum" && value !== "member" && (
        <div className="space-y-2">
          <Label>Host Type</Label>
          <RadioGroup value={hostType} onValueChange={onHostTypeChange} className="flex space-x-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="member" id="member" />
              <Label htmlFor="member" className="cursor-pointer">Member Host</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="artist" id="artist" />
              <Label htmlFor="artist" className="cursor-pointer">Artist Host</Label>
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  );
};

export default HostSelect;
